const SUPPORTED_MEAL_TYPES = [
  'breakfast',
  'lunch',
  'dinner',
  'snack',
  'dessert',
  'other',
] as const;

type SupportedMealType = (typeof SUPPORTED_MEAL_TYPES)[number];

const SUPPORTED_MEAL_TYPE_SET = new Set<string>(SUPPORTED_MEAL_TYPES);

export const cleanMealLabel = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim();
};

const tokenizeMealLabel = (value: unknown): string[] => {
  const clean = cleanMealLabel(value).toLowerCase();
  if (!clean) return [];
  return clean
    .split(/[/|,&]+/)
    .map((token) => token.trim())
    .filter(Boolean);
};

export const resolveMealDisplayKey = (
  mealName: unknown,
  mealType: unknown,
  fallbackKey: string
): string => {
  const cleanMealName = cleanMealLabel(mealName);
  if (cleanMealName) return cleanMealName;

  const cleanMealType = cleanMealLabel(mealType);
  if (cleanMealType) return cleanMealType;

  return fallbackKey;
};

export const mealKeysMatch = (left: unknown, right: unknown): boolean => {
  const leftClean = cleanMealLabel(left);
  const rightClean = cleanMealLabel(right);
  if (!leftClean || !rightClean) return false;
  if (leftClean.toLowerCase() === rightClean.toLowerCase()) return true;

  const leftTokens = tokenizeMealLabel(leftClean);
  const rightTokens = tokenizeMealLabel(rightClean);
  if (leftTokens.length === 0 || rightTokens.length === 0) return false;

  return leftTokens.some((token) => rightTokens.includes(token));
};

export const normalizeMealTypeForSelect = (
  value: unknown
): SupportedMealType => {
  const clean = cleanMealLabel(value).toLowerCase();
  if (!clean) return 'other';

  if (SUPPORTED_MEAL_TYPE_SET.has(clean)) {
    return clean as SupportedMealType;
  }

  const tokens = tokenizeMealLabel(clean);
  for (const token of tokens) {
    if (SUPPORTED_MEAL_TYPE_SET.has(token)) {
      return token as SupportedMealType;
    }
  }

  if (clean.includes('breakfast')) return 'breakfast';
  if (clean.includes('lunch')) return 'lunch';
  if (clean.includes('dinner') || clean.includes('supper')) return 'dinner';
  if (clean.includes('snack')) return 'snack';
  if (clean.includes('dessert')) return 'dessert';
  return 'other';
};

export const getMealOrderByMealKey = (
  meals: Record<string, { mealOrder?: number } | undefined> | undefined,
  mealKey: unknown
): number | undefined => {
  if (!meals) return undefined;

  const cleanKey = cleanMealLabel(mealKey);
  if (!cleanKey) return undefined;

  const entries = Object.entries(meals);
  const exactMatchIndex = entries.findIndex(
    ([key]) => key.toLowerCase() === cleanKey.toLowerCase()
  );
  if (exactMatchIndex >= 0) {
    const exactMealOrder = entries[exactMatchIndex][1]?.mealOrder;
    return typeof exactMealOrder === 'number' && exactMealOrder > 0
      ? exactMealOrder
      : exactMatchIndex + 1;
  }

  const matchingIndex = entries.findIndex(([key]) =>
    mealKeysMatch(key, cleanKey)
  );
  if (matchingIndex < 0) return undefined;

  const matchedMealOrder = entries[matchingIndex][1]?.mealOrder;
  return typeof matchedMealOrder === 'number' && matchedMealOrder > 0
    ? matchedMealOrder
    : matchingIndex + 1;
};
